import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import N8nStyleCanvas from './N8nStyleCanvasV2';
import { Workflow } from '../types/workflow';
import { Save, Download, ArrowLeft, Undo2, Redo2 } from 'lucide-react';
import Logo from './Logo';
import { CONSTANT_STEPS } from '../constants/stepTemplates';
import { cn } from '../utils/cn';
import { useProject } from '../context/ProjectContext';
import { updateSystemNodesWithProjectData } from '../utils/systemNodesFiller';
import { slugify } from '../utils/slug';
import { useUndoRedo } from '../hooks/useUndoRedo';

const STORAGE_KEY = 'dsl-workflow-autosave';

interface WorkflowStudioProps {}

function WorkflowStudio({}: WorkflowStudioProps) {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { project } = useProject();
  
  // Helper to normalize any arbitrary value into a valid Workflow
  const normalizeWorkflow = (wf: any): Workflow => {
    const defaultWf: Workflow = {
      chain_id: 11155111,
      sender: "{{ENV.SENDER_ADDRESS}}",
      delegate: "{{TRANSACTION_INTENT_DELEGATE}}",
      attestor: "{{ENV.ATTESTOR_IMAGE}}",
      target: { contract: "", function: "", authData_result: "0x00", parameters: [] },
      sponsor_execution_fee: true,
      value: "0",
      intent: { id: "{{TRANSACTION_INTENT_ID}}", signature: "{{USER_SIGNATURE}}", deadline: "{{TRANSACTION_INTENT_DEADLINE}}" },
      rpc_url: "${_SECRETS.rpcSepoliaURL}",
      bundler_url: "https://api.pimlico.io/v2/sepolia/rpc?apikey=${_SECRETS.pimlico-apikey}",
      paymaster_url: "https://api.pimlico.io/v2/sepolia/rpc?apikey=${_SECRETS.pimlico-apikey}",
      gas_limit: "100000",
      max_fee_per_gas: "20000000000",
      max_priority_fee_per_gas: "2000000000",
      workflow: { name: "", version: "v1.0.0", steps: [] },
      dapp: { name: "", targetChainID: 1, targetContractAddress: "", targetFunctionSignature: "" },
      // executionFee is legacy; keep if present elsewhere
    } as any;

    const result: any = { ...defaultWf, ...(wf || {}) };
    // Normalize nested sections
    result.workflow = {
      name: wf?.workflow?.name || result.workflow.name,
      version: wf?.workflow?.version || result.workflow.version,
      steps: Array.isArray(wf?.workflow?.steps) ? wf.workflow.steps : [],
    };
    result.target = {
      contract: wf?.target?.contract ?? result.target.contract,
      function: wf?.target?.function ?? result.target.function,
      authData_result: typeof wf?.target?.authData_result !== 'undefined' ? wf.target.authData_result : result.target.authData_result,
      parameters: Array.isArray(wf?.target?.parameters) ? wf.target.parameters : [],
    };
    result.intent = wf?.intent || result.intent;
    return result as Workflow;
  };

  // Get initial workflow from storage
  const getInitialWorkflow = (): Workflow => {
    // First try to load current project
    try {
      const currentProject = localStorage.getItem('current-project');
      if (currentProject) {
        const project = JSON.parse(currentProject);
        const wf = project?.workflow;
        if (wf) return normalizeWorkflow(wf);
      }
    } catch (error) {
      console.warn('Failed to load current project:', error);
    }
    
    // Then try autosave
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const wf = JSON.parse(saved);
        if (wf) return normalizeWorkflow(wf);
      }
    } catch (error) {
      console.warn('Failed to load saved workflow:', error);
    }
    
    // Default workflow
    return normalizeWorkflow(undefined);
  };

  // Use undo/redo hook for workflow state management
  const {
    state: workflow,
    setState: setWorkflow,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo<Workflow>(getInitialWorkflow(), {
    maxHistorySize: 50,
  });

  // Always normalize any state before pushing into history
  const setWorkflowSafe = useCallback((next: Workflow | ((prev: Workflow) => Workflow)) => {
    if (typeof next === 'function') {
      setWorkflow((prev) => normalizeWorkflow((next as (p: Workflow) => Workflow)(normalizeWorkflow(prev))));
    } else {
      setWorkflow(normalizeWorkflow(next));
    }
  }, [setWorkflow]);

  // Debounced save function
  const saveToLocalStorage = useCallback((workflowData: Workflow) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(workflowData));
    } catch (error) {
      console.warn('Failed to save workflow to localStorage:', error);
    }
  }, []);

  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Update workflow with project data when available
  useEffect(() => {
    if (project && project.contractABI) {
      setWorkflowSafe(prevWorkflow => {
        // Normalize prevWorkflow first to ensure it has proper structure
        const normalized = normalizeWorkflow(prevWorkflow);
        
        // Always update target and dapp fields with project data
        const updatedWorkflow = {
          ...normalized,
          workflow: {
            ...normalized.workflow,
            name: project.name || normalized.workflow?.name || '',
          },
          target: {
            ...normalized.target,
            contract: project.contractAddress || normalized.target?.contract || '',
            function: project.selectedFunction || normalized.target?.function || '',
          },
          dapp: {
            name: project.name || normalized.workflow?.name || normalized.dapp?.name || '',
            targetChainID: project.chainId || normalized.dapp?.targetChainID || 1,
            ...normalized.dapp,
            targetContractAddress: project.contractAddress || normalized.dapp?.targetContractAddress || '',
            targetFunctionSignature: project.selectedFunction || normalized.dapp?.targetFunctionSignature || '',
          },
        };

        const existingSteps = normalized.workflow.steps || [];
        const customSteps = existingSteps.filter(step => !CONSTANT_STEPS.some(cs => cs.name === step.name));
        const hasCustom = customSteps.length > 0;
        const hasConstruct = existingSteps.some(s => (s.image || '').includes('executor-encoder-evm') || s.name === 'construct-evm');
        
        // Only update system steps if there are custom steps or construct present
        if (!hasCustom && !hasConstruct) {
          return updatedWorkflow;
        }

        const updatedSystemSteps = updateSystemNodesWithProjectData([...CONSTANT_STEPS], project);
        const withoutSystem = existingSteps.filter(step => !CONSTANT_STEPS.some(cs => cs.name === step.name));

        return {
          ...updatedWorkflow,
          workflow: {
            ...normalized.workflow,
            steps: [
              ...withoutSystem,
              ...updatedSystemSteps
            ]
          }
        };
      });
    }
  }, [project, setWorkflowSafe, normalizeWorkflow]);

  // Auto-save project to projects list when changes are made
  const autoSaveProject = useCallback(() => {
    try {
      const currentProject = localStorage.getItem('current-project');
      if (currentProject) {
        // Update existing current project
        const project = JSON.parse(currentProject);
        const updatedProject = {
          ...project,
          workflow,
          modifiedAt: new Date(),
          name: workflow.workflow.name || project.name || 'Untitled Workflow'
        };
        localStorage.setItem('current-project', JSON.stringify(updatedProject));
        
        // Also update in projects list
        const projects = JSON.parse(localStorage.getItem('dsl-workflow-projects') || '[]');
        const projectIndex = projects.findIndex((p: any) => p.id === project.id);
        if (projectIndex !== -1) {
          projects[projectIndex] = updatedProject;
          localStorage.setItem('dsl-workflow-projects', JSON.stringify(projects));
        } else {
          // If project not found in list, add it (this handles edge cases)
          console.warn('Project not found in projects list, adding it:', project.id);
          const newProjects = [updatedProject, ...projects];
          localStorage.setItem('dsl-workflow-projects', JSON.stringify(newProjects));
        }
      } else {
        console.warn('No current-project found in localStorage, auto-save skipped');
      }
    } catch (error) {
      console.warn('Failed to auto-save project:', error);
    }
  }, [workflow]);


  // Auto-save with debouncing
  useEffect(() => {
    setIsSaving(true);
    const timeoutId = setTimeout(() => {
      saveToLocalStorage(workflow);
      autoSaveProject(); // Auto-save to projects list
      setLastSaved(new Date());
      setIsSaving(false);
    }, 1000); // Save after 1 second of inactivity

    return () => clearTimeout(timeoutId);
  }, [workflow, saveToLocalStorage, autoSaveProject]);

  const clearSavedData = (): void => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setLastSaved(null);
      // Reset to default workflow with new DSL structure
      const defaultWorkflow: Workflow = {
        chain_id: 11155111,
        sender: "{{ENV.SENDER_ADDRESS}}",
        delegate: "{{TRANSACTION_INTENT_DELEGATE}}",
        attestor: "{{ENV.ATTESTOR_IMAGE}}",
        target: {
          contract: "{{ENV.TARGET_CONTRACT}}",
          function: "",
          authData_result: "0x00",
          parameters: []
        },
        sponsor_execution_fee: true,
        value: "0",
        intent: {
          id: "{{TRANSACTION_INTENT_ID}}",
          signature: "{{USER_SIGNATURE}}",
          deadline: "{{TRANSACTION_INTENT_DEADLINE}}"
        },
        rpc_url: "${_SECRETS.rpcSepoliaURL}",
        bundler_url: "https://api.pimlico.io/v2/sepolia/rpc?apikey=${_SECRETS.pimlico-apikey}",
        paymaster_url: "https://api.pimlico.io/v2/sepolia/rpc?apikey=${_SECRETS.pimlico-apikey}",
        gas_limit: "100000",
        max_fee_per_gas: "20000000000",
        max_priority_fee_per_gas: "2000000000",
        workflow: {
          name: "",
          version: "v1.0.0",
          steps: []
        },
        dapp: {
          name: "",
          targetChainID: 1,
          targetContractAddress: "",
          targetFunctionSignature: ""
        }
      };
      setWorkflowSafe(defaultWorkflow);
      // Force immediate clear of localStorage to prevent auto-save race condition
      setTimeout(() => localStorage.removeItem(STORAGE_KEY), 0);
    } catch (error) {
      console.warn('Failed to clear saved data:', error);
    }
  };

  // Helper to identify construct-evm nodes properly
  const isConstructEvmNode = (step: any): boolean => {
    return (
      step.image === 'ghcr.io/krnl-labs/executor-encoder-evm@sha256:b28823d12eb1b16cbcc34c751302cd2dbe7e35480a5bc20e4e7ad50a059b6611' &&
      step.next === 'prepare-authdata'
    );
  };

  const downloadJson = (): void => {
    // Separate custom workflow steps, construct-evm, and other system steps
    const stepsAll = workflow.workflow.steps;
    const systemStepNames = CONSTANT_STEPS.map(s => s.name);
    
    // Separate steps by type using proper identification
    const customSteps = stepsAll.filter(step => 
      !systemStepNames.includes(step.name) && !isConstructEvmNode(step)
    );
    
    // Only identify construct step if there are custom steps (workflow mode)
    // In empty workflow mode, there should be no construct step
    const constructStep = customSteps.length > 0 
      ? stepsAll.find(step => isConstructEvmNode(step)) || null
      : null;
    // Identify system nodes individually to enforce order
    const prepareStep = stepsAll.find(s => s.name === 'prepare-authdata') || null;
    const targetCalldataStep = stepsAll.find(s => s.name === 'target-calldata') || null;
    const scaStep = stepsAll.find(s => s.name === 'sca-calldata') || null;
    const bundleStep = stepsAll.find(s => s.name === 'bundle') || null;

    // Create export workflow with custom steps first, then prepare, then construct (target-calldata), then sca, then bundle
    // Ensure we do NOT duplicate target-calldata by excluding it from generic system list
    // Order rules:
    // - If there are workflow (custom) steps: custom -> construct -> prepare -> target-calldata -> sca -> bundle
    // - If there are no custom steps: prepare -> target-calldata -> sca -> bundle
    const orderedSteps = (
      customSteps.length > 0
        ? [
            ...customSteps,
            ...(constructStep ? [constructStep] : []),
            ...(prepareStep ? [prepareStep] : []),
            ...(targetCalldataStep ? [targetCalldataStep] : []),
            ...(scaStep ? [scaStep] : []),
            ...(bundleStep ? [bundleStep] : []),
          ]
        : [
            ...(prepareStep ? [prepareStep] : []),
            ...(targetCalldataStep ? [targetCalldataStep] : []),
            ...(scaStep ? [scaStep] : []),
            ...(bundleStep ? [bundleStep] : []),
          ]
    );

    // Normalize system nodes on export
    // Secondary check: only count construct step present if it's actually part of the export order
    const hasConstructInExport = !!constructStep && orderedSteps.includes(constructStep);
    // Empty workflow mode: no custom steps and no construct
    const isEmptyWorkflow = customSteps.length === 0 && !constructStep;

    // Helper to normalize a single step, parameterized on whether to include `type`
    const normalizeStep = (step: any, includeType: boolean) => {
      const isSystem = systemStepNames.includes(step.name);

      // Normalize prepare-authdata
      if (step.name === 'prepare-authdata') {

      // Find current result input value if present
      const resultInput = Array.isArray(step.inputs)
        ? step.inputs.find((i: any) => i.name === 'result')
        : undefined;
      const hasConstructStep = hasConstructInExport;
      const resultValue = hasConstructStep
        ? (resultInput?.value ?? `\${${constructStep.name}.result}`)
        : '0x00';

      // Build inputs object as required
      const inputsObject = {
        result: resultValue,
        executions: [] as any[]
      };

      // For export: outputs depend on workflow type
      const outputs = (step.outputs || []).map((o: any) => {
        if (isEmptyWorkflow) {
          // Empty workflow: use type field instead of value
          return {
            name: o.name,
            type: o.type || 'string',
            required: o.required,
            export: o.export,
          };
        } else {
          // Workflow with steps: use value field
          return {
            name: o.name,
            value: o.value || o.name,
            required: o.required,
            export: o.export,
          };
        }
      });

      const configWithoutParams = { ...(step.config as any) };
      if ('parameters' in configWithoutParams) {
        delete (configWithoutParams as any).parameters;
      }

      // Create step object with type as first property if needed
      const prepareStep: any = {};
      
      // Include type field if requested (for first step in empty workflow)
      if (includeType) {
        prepareStep.type = 10;
      }
      
      // Add other properties
      prepareStep.name = step.name;
      prepareStep.image = (step as any).image;
      prepareStep.attestor = (step as any).attestor;
      prepareStep.next = (step as any).next;
      prepareStep.config = configWithoutParams;
      prepareStep.inputs = inputsObject;
      prepareStep.outputs = outputs;
      
      return prepareStep;
      }

      // Normalize target-calldata inputs to required nested object shape (but skip if this is the designated construct step)
      if (step.name === 'target-calldata' && step !== constructStep) {
        let inputsObject: any;

        if (Array.isArray((step as any).inputs)) {
          const arr = (step as any).inputs as any[];
          // If parser already produced a single 'value' object input, unwrap it
          const valueItem = arr.find(i => i.name === 'value');
          if (valueItem && typeof valueItem.value === 'object' && valueItem.value !== null) {
            inputsObject = { value: valueItem.value };
          } else {
            // Fallback: build map from array pairs
            const inputMap: Record<string, any> = {};
            arr.forEach((inp: any) => {
              inputMap[inp.name] = inp.value;
            });
            inputsObject = { value: inputMap };
          }
        } else if ((step as any).inputs && typeof (step as any).inputs === 'object') {
          // Already object-shaped; use as-is
          inputsObject = (step as any).inputs;
        } else {
          inputsObject = {};
        }

        // Create step object with type as first property if needed
        const targetStep: any = {};
        
        // Include type field if requested (for first step in empty workflow)
        if (includeType) {
          targetStep.type = 10;
        }
        
        // Add other properties
        targetStep.name = step.name;
        targetStep.image = (step as any).image;
        targetStep.attestor = (step as any).attestor;
        targetStep.next = (step as any).next;
        targetStep.config = step.config;
        targetStep.inputs = inputsObject as any;
        // System node outputs depend on workflow type
        targetStep.outputs = (step.outputs || []).map((o: any) => {
          if (isEmptyWorkflow) {
            // Empty workflow: use type field instead of value
            return { name: o.name, type: o.type || 'string', required: o.required, export: o.export };
          } else {
            // Workflow with steps: use value field
            return { name: o.name, value: o.value || o.name, required: o.required, export: o.export };
          }
        });
        
        return targetStep;
      }

      // Normalize construct step (may be renamed): keep config exactly as filled; only normalize inputs shape
      if (step === constructStep) {
        let inputsObject: any;
        if (Array.isArray((step as any).inputs)) {
          const arr = (step as any).inputs as any[];
          const valueItem = arr.find(i => i.name === 'value');
          if (valueItem && typeof valueItem.value === 'object' && valueItem.value !== null) {
            inputsObject = { value: valueItem.value };
          } else if (valueItem) {
            // If value exists but is not an object, wrap it
            inputsObject = { value: valueItem.value || {} };
          } else {
            // No value field found, build from all inputs
            const inputMap: Record<string, any> = {};
            arr.forEach((inp: any) => {
              inputMap[inp.name] = inp.value;
            });
            inputsObject = { value: inputMap };
          }
        } else if ((step as any).inputs && typeof (step as any).inputs === 'object') {
          // Already object-shaped
          inputsObject = (step as any).inputs;
          // Ensure it has a value field
          if (!inputsObject.value) {
            inputsObject = { value: inputsObject };
          }
        } else {
          inputsObject = { value: {} };
        }

        return {
          name: step.name,
          image: (step as any).image,
          attestor: (step as any).attestor,
          next: (step as any).next,
          config: step.config,
          inputs: inputsObject as any,
          outputs: (step.outputs || []).map((o: any) => ({ name: o.name, value: o.value, required: o.required, export: o.export })),
        } as any;
      }

      // Normalize sca-calldata & bundle inputs similarly: { value: { ... } }
      if (step.name === 'sca-calldata') {
        const inputMap: Record<string, any> = {};
        (step.inputs || []).forEach((inp: any) => {
          inputMap[inp.name] = inp.value;
        });

        const inputsObject = { value: inputMap };

        // Create step object with type as first property if needed
        const scaStep: any = {};
        
        // Include type field if requested (for first step in empty workflow)
        if (includeType) {
          scaStep.type = 10;
        }
        
        // Add other properties
        scaStep.name = step.name;
        scaStep.image = (step as any).image;
        scaStep.attestor = (step as any).attestor;
        scaStep.next = (step as any).next;
        scaStep.config = step.config;
        scaStep.inputs = inputsObject as any;
        scaStep.outputs = (step.outputs || []).map((o: any) => {
          if (isEmptyWorkflow) {
            // Empty workflow: use type field instead of value
            return { name: o.name, type: o.type || 'string', required: o.required, export: o.export };
          } else {
            // Workflow with steps: use value field
            return { name: o.name, value: o.value || o.name, required: o.required, export: o.export };
          }
        });
        
        return scaStep;
      }

      // Bundle: inputs should be a flat object, omit next, and error output should not include required/export
      if (step.name === 'bundle') {
        const inputObj: Record<string, any> = {};
        (step.inputs || []).forEach((inp: any) => {
          inputObj[inp.name] = inp.value;
        });

        const normalizedOutputs = (step.outputs || []).map((o: any) => {
          if (o.name === 'error') {
            if (isEmptyWorkflow) {
              // Empty workflow: error output only needs name and type
              return { name: 'error', type: 'string' };
            } else {
              // Workflow with steps: error output needs name, value, and type
              return { name: 'error', value: 'error', type: 'string' };
            }
          }
          if (isEmptyWorkflow) {
            // Empty workflow: use type field instead of value for non-error outputs
            return { name: o.name, type: o.type || 'string', required: o.required, export: o.export };
          } else {
            // Workflow with steps: include value for non-error outputs (no type)
            return { name: o.name, value: o.value || o.name, required: o.required, export: o.export };
          }
        });

        // Create step object with type as first property if needed
        const bundleStep: any = {};
        
        // Include type field if requested (for first step in empty workflow)
        if (includeType) {
          bundleStep.type = 10;
        }
        
        // Add other properties
        bundleStep.name = step.name;
        bundleStep.image = (step as any).image;
        bundleStep.attestor = (step as any).attestor;
        // omit next for bundle step
        bundleStep.config = step.config;
        bundleStep.inputs = inputObj as any;
        bundleStep.outputs = normalizedOutputs;
        
        return bundleStep;
      }

      // For any other system node, strip outputs.value
      if (isSystem) {
        return {
          name: step.name,
          image: (step as any).image,
          attestor: (step as any).attestor,
          next: (step as any).next,
          config: step.config,
          inputs: (step as any).inputs,
          outputs: (step.outputs || []).map((o: any) => ({ name: o.name, value: o.value || o.name, required: o.required, export: o.export })),
        } as any;
      }

      // Non-system (custom) steps: export inputs as an object
      let inputObj: Record<string, any> = {};
      if (Array.isArray((step as any).inputs)) {
        // Export all inputs in the same order they appear, preserving field names and values
        (step as any).inputs.forEach((inp: any) => {
          inputObj[inp.name] = inp.value;
        });
      } else if ((step as any).inputs && typeof (step as any).inputs === 'object') {
        // Already object-shaped: export as-is to preserve all fields
        inputObj = (step as any).inputs;
      }

      // For custom steps: include `type` only when requested (first custom step)
      if (includeType) {
        // Construct with `type` first
        const isAI = String((step as any).name || '').toLowerCase().includes('ai');
        return {
          type: (typeof (step as any).type === 'number') ? (step as any).type : Number((step as any).type),
          name: (step as any).name,
          image: (step as any).image,
          attestor: (step as any).attestor,
          next: (step as any).next,
          config: (step as any).config,
          inputs: inputObj as any,
          outputs: (step.outputs || []).map((o: any) => {
            const output: any = {
              name: isAI && o.name === 'response' ? 'result' : o.name,
              value: o.value,
              required: o.required,
              export: o.export,
            };
            // Only AI steps carry JSON type on outputs
            if (isAI) {
              output.type = 'json';
            }
            return output;
          }),
        } as any;
      }
      // No `type` field
      {
        const isAI = String((step as any).name || '').toLowerCase().includes('ai');
        return {
          name: (step as any).name,
          image: (step as any).image,
          attestor: (step as any).attestor,
          next: (step as any).next,
          config: (step as any).config,
          inputs: inputObj as any,
          outputs: (step.outputs || []).map((o: any) => {
            const output: any = {
              name: isAI && o.name === 'response' ? 'result' : o.name,
              value: o.value,
              required: o.required,
              export: o.export,
            };
            // Only AI steps carry JSON type on outputs
            if (isAI) {
              output.type = 'json';
            }
            return output;
          }),
        } as any;
      }
    };

    // Build normalized steps array
    // - Workflow DSL: first custom step gets `type: 10`
    // - Empty DSL: first system step gets `type: 10`
    let normalizedSteps: any[] = [];
    let firstCustomEmitted = false;
    for (let i = 0; i < orderedSteps.length; i++) {
      const step = orderedSteps[i];
      const isCustom = !systemStepNames.includes((step as any).name);
      const isFirstStep = i === 0;
      
      // Include type field for:
      // - First custom step in workflow DSL
      // - First step (system step) in empty DSL
      const includeType = (isCustom && !firstCustomEmitted) || (isEmptyWorkflow && isFirstStep);
      if (isCustom && !firstCustomEmitted) firstCustomEmitted = true;
      
      const normalizedStep = normalizeStep(step, includeType);
      
      // Fix next field to ensure proper linking in export
      if (i < orderedSteps.length - 1) {
        const nextStep = orderedSteps[i + 1];
        normalizedStep.next = nextStep.name;
      } else {
        // Last step should not have a next field or should point to appropriate end
        if (normalizedStep.name === 'bundle') {
          // Bundle step omits next field
          delete normalizedStep.next;
        }
      }
      // Sanitize based on mode
      const isSystemExport = systemStepNames.includes(normalizedStep.name);
      if (isSystemExport) {
        // Remove type field for system steps (unless already added by step normalization)
        if (!includeType && 'type' in normalizedStep) {
          delete (normalizedStep as any).type;
        }
        if (Array.isArray(normalizedStep.outputs)) {
          if (isEmptyWorkflow) {
            // For empty workflow DSLs: export outputs with `type` instead of `value`
            // Prefer canonical definitions to guarantee correct types
            const canonical = CONSTANT_STEPS.find(cs => cs.name === normalizedStep.name) as any;
            const sourceOutputs: any[] = Array.isArray(canonical?.outputs)
              ? canonical.outputs
              : (Array.isArray((step as any).outputs) ? (step as any).outputs : []);
            normalizedStep.outputs = sourceOutputs.map((o: any) => {
              // Special case: bundle error output should only have name and type
              if (normalizedStep.name === 'bundle' && o.name === 'error') {
                return { name: 'error', type: 'string' };
              }
              
              const out: any = {
                name: o.name,
                type: o.type,
                required: o.required,
                export: o.export,
              };
              // Hard remove any accidental value fields
              if ('value' in out) delete out.value;
              return out;
            });
          } else {
            // For DSLs with workflow: remove type and ensure value
            normalizedStep.outputs = normalizedStep.outputs.map((o: any) => {
              const out: any = { ...o };
              const isBundleError = normalizedStep.name === 'bundle' && out.name === 'error';
              if (!isBundleError && 'type' in out) delete out.type;
              if (typeof out.value === 'undefined') out.value = out.name;
              return out;
            });
          }
        }
      }

      normalizedSteps.push(normalizedStep);
    }
    // Do not auto-inject construct-evm here; the canvas manages it. If a prepare-authdata exists and its inputs are array-shaped,
    // keep existing normalization above which already sets inputs object with result/executions.

    // Final enforcement: for empty workflow DSLs, force system outputs to be type-only based on canonical templates
    if (isEmptyWorkflow) {
      const systemNamesSet = new Set(systemStepNames);
      normalizedSteps = normalizedSteps.map((ns: any) => {
        if (!systemNamesSet.has(ns.name)) return ns;
        const canonical = CONSTANT_STEPS.find(cs => cs.name === ns.name) as any;
        const src = Array.isArray(canonical?.outputs) ? canonical.outputs : (Array.isArray(ns.outputs) ? ns.outputs : []);
        const fixedOutputs = src.map((o: any) => {
          // Special case: bundle error output should only have name and type
          if (ns.name === 'bundle' && o.name === 'error') {
            return { name: 'error', type: 'string' };
          }
          
          return {
            name: o.name,
            type: o.type,
            required: o.required,
            export: o.export,
          };
        });
        // Hard delete any residual 'value' keys just in case
        const cleaned = fixedOutputs.map((o: any) => {
          const c: any = { ...o };
          if ('value' in c) delete c.value;
          return c;
        });
        return { ...ns, outputs: cleaned };
      });
    }

    // Derive top-level DSL fields from system nodes
    const bundleNode = stepsAll.find(s => s.name === 'bundle') as any || {};
    const targetCalldataNode = stepsAll.find(s => s.name === 'target-calldata') as any || {};
    const prepareNode = stepsAll.find(s => s.name === 'prepare-authdata') as any || {};

    const bundleCfg = (bundleNode?.config || {}) as any;
    const targetCfg = (targetCalldataNode?.config || {}) as any;
    const prepareCfg = (prepareNode?.config || {}) as any;

    // Determine target.authData_result: construct result in workflow mode, otherwise 0x00
    const topLevelAuthDataResult = customSteps.length > 0 && constructStep
      ? `\${${constructStep.name}.result}`
      : '0x00';

    const exportWorkflow = {
      // New top-level DSL fields
      chain_id: bundleCfg.chain_id ?? workflow.chain_id,
      sender: bundleCfg.sender ?? workflow.sender,
      delegate: workflow.delegate,
      attestor: workflow.attestor,
      target: {
        contract: bundleCfg.target_contract ?? workflow.dapp?.targetContractAddress ?? workflow.target?.contract ?? '',
        function: targetCfg.function_signature ?? workflow.dapp?.targetFunctionSignature ?? workflow.target?.function ?? '',
        authData_result: topLevelAuthDataResult,
        parameters: workflow.target?.parameters ?? (Array.isArray(targetCfg.parameters) && targetCfg.parameters.length > 1
          ? (targetCfg.parameters as any[]).slice(1)
          : [])
      },
      sponsor_execution_fee: prepareCfg.sponsor_execution_fee ?? workflow.sponsor_execution_fee ?? true,
      value: workflow.value ?? '0',
      intent: workflow.intent ?? {
        id: '${TRANSACTION_INTENT_ID}',
        signature: '${USER_SIGNATURE}',
        deadline: '${TRANSACTION_INTENT_DEADLINE}',
      },
      rpc_url: bundleCfg.rpc_url ?? workflow.rpc_url,
      bundler_url: bundleCfg.bundler_url ?? workflow.bundler_url,
      paymaster_url: bundleCfg.paymaster_url ?? workflow.paymaster_url,
      gas_limit: bundleCfg.gas_limit ?? workflow.gas_limit,
      max_fee_per_gas: bundleCfg.max_fee_per_gas ?? workflow.max_fee_per_gas,
      max_priority_fee_per_gas: bundleCfg.max_priority_fee_per_gas ?? workflow.max_priority_fee_per_gas,
      // Workflow steps (custom steps + construct step if exists)
      workflow: {
        name: workflow.workflow.name,
        version: workflow.workflow.version,
        steps: [...customSteps, ...(constructStep ? [constructStep] : [])].map(step => {
          // Clean up step structure for export
          const cleanStep: any = {
            name: step.name,
            image: step.image,
            attestor: step.attestor,
            ...(step.next && { next: step.next }),
            ...(step.config && Object.keys(step.config).length > 0 && { config: step.config })
          };

          // Handle inputs - convert array format to object format if needed
          if (Array.isArray(step.inputs)) {
            const inputsObject: any = {};
            step.inputs.forEach((input: any) => {
              inputsObject[input.name] = input.value;
            });
            cleanStep.inputs = inputsObject;
          } else if (step.inputs && typeof step.inputs === 'object' && !Array.isArray(step.inputs)) {
            // Clean up inputs object to remove type fields and other metadata
            const cleanInputs: any = {};
            const inputsRecord = step.inputs as Record<string, any>;
            Object.keys(inputsRecord).forEach(key => {
              if (typeof inputsRecord[key] === 'object' && inputsRecord[key] !== null && 'value' in inputsRecord[key]) {
                // If it's an input object with value property, extract just the value
                cleanInputs[key] = inputsRecord[key].value;
              } else {
                // Otherwise use the value directly
                cleanInputs[key] = inputsRecord[key];
              }
            });
            cleanStep.inputs = cleanInputs;
          }

          // Handle outputs - remove type field and other unnecessary metadata
          if (step.outputs && step.outputs.length > 0) {
            cleanStep.outputs = step.outputs.map((output: any) => {
              const cleanOutput: any = {
                name: output.name,
                value: output.value,
                required: output.required,
                export: output.export
              };
              // Only include fields that have meaningful values
              return Object.fromEntries(
                Object.entries(cleanOutput).filter(([_, value]) => value !== undefined)
              );
            });
          }

          return cleanStep;
        })
      }
    } as any;
    const dataStr = JSON.stringify(exportWorkflow, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slugify(workflow.workflow.name || 'workflow')}.json`;
    link.click();
  };


  return (
    <div className="min-h-screen bg-n8n-dark-950 text-n8n-dark-200">
      {/* Sharp SaaS Header */}
      <header className="bg-n8n-dark-800/95 backdrop-blur-xl border-b border-n8n-dark-600/50 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2.5 hover:bg-n8n-dark-700/50 rounded-lg transition-all duration-200 text-n8n-dark-300 hover:text-n8n-primary-400 hover:scale-105 border border-transparent hover:border-n8n-dark-600"
              >
                <ArrowLeft size={18} />
              </button>
              <Logo size="sm" showText={true} />
              <div className="flex items-center space-x-4 ml-4">
                <div className="w-px h-3 bg-n8n-dark-600"></div>
                <div className="flex items-center space-x-2 border border-n8n-dark-600 rounded-lg p-1 w-[9vw] justify-center">
                  {isSaving ? (
                    <div className="flex items-center space-x-1.5 text-n8n-primary-400">
                      <div className="w-1.5 h-1.5 bg-n8n-primary-400 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium">Saving</span>
                    </div>
                  ) : lastSaved ? (
                    <div className="flex items-center space-x-1.5 text-n8n-success-400">
                      <div className="w-1.5 h-1.5 bg-n8n-success-400 rounded-full"></div>
                      <span className="text-xs font-medium">
                        Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
  
            <div className="flex items-center space-x-2">
              {/* Undo/Redo buttons */}
              <div className="flex items-center space-x-1 border border-n8n-dark-600 rounded-lg p-1">
                <button
                  onClick={undo}
                  disabled={!canUndo}
                  title="Undo (Cmd/Ctrl+Z)"
                  className={cn(
                    "p-2 rounded-md transition-all duration-200",
                    canUndo
                      ? "hover:bg-n8n-dark-700 text-n8n-dark-200 hover:text-n8n-primary-400 cursor-pointer"
                      : "text-n8n-dark-600 cursor-not-allowed opacity-50"
                  )}
                >
                  <Undo2 size={16} />
                </button>
                <button
                  onClick={redo}
                  disabled={!canRedo}
                  title="Redo (Cmd/Ctrl+Shift+Z)"
                  className={cn(
                    "p-2 rounded-md transition-all duration-200",
                    canRedo
                      ? "hover:bg-n8n-dark-700 text-n8n-dark-200 hover:text-n8n-primary-400 cursor-pointer"
                      : "text-n8n-dark-600 cursor-not-allowed opacity-50"
                  )}
                >
                  <Redo2 size={16} />
                </button>
              </div>
              <div className="w-px h-6 bg-n8n-dark-600"></div>
              <button
                onClick={clearSavedData}
                className="px-4 py-2 bg-n8n-dark-700/80 hover:bg-n8n-dark-600 border border-n8n-dark-600 hover:border-n8n-dark-500 text-n8n-dark-200 hover:text-n8n-dark-100 rounded-lg transition-all duration-200 font-medium text-sm hover:scale-105 backdrop-blur-sm"
              >
                Clear Data
              </button>
              <div className="w-px h-6 bg-n8n-dark-600"></div>
              <button
                onClick={downloadJson}
                className="px-4 py-2 bg-gradient-to-r from-n8n-primary-500 to-n8n-primary-600 hover:from-n8n-primary-600 hover:to-n8n-primary-700 text-white rounded-lg transition-all duration-200 font-medium text-sm hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2 border border-n8n-primary-400/20"
              >
                <Download size={14} />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Sharp SaaS Main Content */}
      <div className="px-6 py-6 h-[calc(100vh-100px)]">
        <div className="h-full bg-n8n-dark-900/50 rounded-xl border border-n8n-dark-600/50 shadow-2xl backdrop-blur-sm overflow-hidden">
          <N8nStyleCanvas 
            workflow={workflow} 
            setWorkflow={setWorkflowSafe} 
          />
        </div>
      </div>

    </div>
  );
}

export default WorkflowStudio;